import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Brain, Send, Loader2, Sparkles, ArrowLeft, RotateCcw, Zap, Clock, MessageSquare, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const QUICK_ACTIONS = [
  { label: 'Aumentar ventas 10%', instruction: 'Aumenta el volumen de ventas de todos los productos un 10%' },
  { label: 'Agregar un producto', instruction: 'Agrega un nuevo producto complementario al negocio con precio y costos realistas' },
  { label: 'Reducir gastos fijos', instruction: 'Reduce los gastos fijos un 15% optimizando costos donde sea posible' },
  { label: 'Cambiar financiamiento', instruction: 'Cambia la estructura de financiamiento a 70% capital propio y 30% deuda' },
  { label: 'Activar perpetuidad', instruction: 'Activa la perpetuidad con una tasa de crecimiento del 2% y capex de reemplazo del 10%' },
  { label: 'Agregar un empleado', instruction: 'Agrega un empleado adicional con sueldo promedio del mercado incluyendo cargas patronales' },
];

const AIModifyPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, hasActivePlan } = useAuth();
  const { currentProject, fetchProject } = useProject();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [usage, setUsage] = useState(null);
  const [sessionId] = useState(() => `modify-${projectId}-${Date.now()}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!currentProject || currentProject.id !== projectId) {
      fetchProject(projectId);
    }
    fetchHistory();
    fetchUsage();
  }, [projectId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${API}/api/ai-analyst/modify-history/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const fetchUsage = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${API}/api/ai-analyst/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(res.data);
    } catch (e) { /* ignore */ }
  };

  const handleModify = async (instruction) => {
    if (!instruction.trim() || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: instruction }]);
    setLoading(true);

    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(`${API}/api/ai-analyst/modify`, {
        project_id: projectId,
        instruction: instruction.trim(),
        session_id: sessionId
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `He aplicado los cambios a tu proyecto. Los datos se han actualizado.\n\n**Instruccion aplicada:** ${instruction}`
      }]);
      toast.success('Proyecto modificado con exito');

      // Refresh project data in context
      await fetchProject(projectId);
      fetchHistory();
      fetchUsage();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al modificar el proyecto';
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${detail}` }]);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleChat = async (question) => {
    if (!question.trim() || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setLoading(true);

    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(`${API}/api/ai-analyst/chat`, {
        project_id: projectId,
        instruction: question.trim(),
        session_id: sessionId
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error al procesar tu mensaje';
      setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${detail}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    if (!input.trim()) return;
    const text = input.trim();
    const lower = text.toLowerCase();
    // Detect if user wants to modify or just ask
    const modifyKeywords = ['cambia', 'modifica', 'ajusta', 'agrega', 'quita', 'sube', 'baja',
      'aumenta', 'reduce', 'elimina', 'añade', 'activa', 'desactiva', 'pon', 'ponle',
      'mueve', 'renombra', 'sube el', 'baja el', 'incrementa', 'disminuye',
      'duplica', 'triplica', 'cambialo', 'cambiale', 'ponle', 'quitale'];
    const isModify = modifyKeywords.some(k => lower.includes(k));

    if (isModify) {
      handleModify(text);
    } else {
      handleChat(text);
    }
  };

  const projectName = currentProject?.specifications?.name || currentProject?.name || 'Proyecto';

  return (
    <div className="min-h-[calc(100vh-120px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/app/project/${projectId}/dashboard`)}
            data-testid="ai-modify-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
            <Brain className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold" data-testid="ai-modify-title">Modificar con IA</h1>
            <p className="text-xs text-muted-foreground truncate max-w-[300px]">{projectName}</p>
          </div>
        </div>
        {usage && (
          <div className="text-right text-xs text-muted-foreground" data-testid="ai-modify-usage">
            <div className="flex items-center gap-1">
              <Zap className="w-3 h-3 text-amber-400" />
              Modificaciones: {usage.modification.used}/{usage.modification.limit}
              {usage.bonus?.modifications > 0 && <span className="text-amber-400">(+{usage.bonus.modifications} extra)</span>}
            </div>
            {usage.modification.used >= usage.modification.limit - 2 && (
              <button onClick={() => navigate('/pricing')} className="text-amber-400 hover:text-amber-300 underline mt-0.5 transition-colors">Recargar</button>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-5 flex-1 min-h-0">
        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Messages */}
          <Card className="flex-1 flex flex-col min-h-0 border-border/40 overflow-hidden">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ai-modify-messages">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <div className="w-16 h-16 rounded-2xl bg-violet-500/10 flex items-center justify-center mb-5">
                    <Sparkles className="w-8 h-8 text-violet-400" />
                  </div>
                  <h2 className="text-base font-semibold mb-2">Modifica tu proyecto con lenguaje natural</h2>
                  <p className="text-sm text-muted-foreground max-w-md mb-6">
                    Escribe una instruccion y la IA aplicara los cambios a tu modelo financiero.
                    Tambien puedes hacer preguntas sobre tu proyecto.
                  </p>
                  <div className="grid grid-cols-2 gap-2 max-w-lg w-full">
                    {QUICK_ACTIONS.slice(0, 4).map((qa) => (
                      <button
                        key={qa.label}
                        onClick={() => handleModify(qa.instruction)}
                        disabled={loading}
                        className="text-left p-3 rounded-lg border border-border/30 hover:border-violet-500/40 hover:bg-violet-500/5 transition-all text-xs text-muted-foreground group"
                        data-testid={`ai-quick-${qa.label.replace(/\s/g, '-').toLowerCase()}`}
                      >
                        <ChevronRight className="w-3 h-3 inline mr-1 text-violet-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        {qa.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-4 py-3 rounded-xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-violet-600 text-white rounded-br-sm'
                      : 'bg-[#161b2e] text-gray-200 rounded-bl-sm border border-border/20'
                  }`}>
                    {msg.role === 'assistant' ? (
                      <div className="prose prose-sm prose-invert max-w-none [&>p]:mb-1.5 [&>ul]:mb-1.5 [&>ul]:ml-4 [&>p:last-child]:mb-0">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    ) : msg.content}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="bg-[#161b2e] border border-border/20 px-4 py-3 rounded-xl rounded-bl-sm flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-violet-400" />
                    <span className="text-xs text-gray-400">Aplicando cambios...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-border/30">
              <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ej: Aumenta las ventas un 15% o pregunta sobre tus metricas..."
                  className="flex-1 bg-background border border-border/50 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/40 placeholder:text-muted-foreground/50"
                  disabled={loading}
                  data-testid="ai-modify-input"
                />
                <Button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="px-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
                  data-testid="ai-modify-send-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </form>
              <p className="text-[11px] text-muted-foreground/60 mt-2 px-1">
                Instrucciones de cambio: la IA modifica tu proyecto. Preguntas: la IA analiza sin modificar.
              </p>
            </div>
          </Card>
        </div>

        {/* Sidebar with quick actions + history */}
        <div className="w-64 flex-shrink-0 space-y-4 hidden lg:block">
          {/* Quick Actions */}
          <Card className="p-4 border-border/40">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Zap className="w-3 h-3 text-amber-400" />
              Acciones rapidas
            </h3>
            <div className="space-y-1.5">
              {QUICK_ACTIONS.map((qa) => (
                <button
                  key={qa.label}
                  onClick={() => handleModify(qa.instruction)}
                  disabled={loading}
                  className="w-full text-left px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-violet-500/10 border border-transparent hover:border-violet-500/20 transition-all disabled:opacity-50"
                  data-testid={`ai-sidebar-${qa.label.replace(/\s/g, '-').toLowerCase()}`}
                >
                  {qa.label}
                </button>
              ))}
            </div>
          </Card>

          {/* History */}
          {history.length > 0 && (
            <Card className="p-4 border-border/40">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-blue-400" />
                Historial
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.slice(0, 10).map((log, i) => (
                  <div key={i} className="text-xs text-muted-foreground/80 px-2 py-1.5 rounded bg-background/50 border border-border/20">
                    <p className="truncate">{log.instruction}</p>
                    <p className="text-[10px] text-muted-foreground/50 mt-0.5">
                      {new Date(log.timestamp).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Token info */}
          <Card className="p-4 border-border/40 bg-amber-500/5 border-amber-500/20">
            <h3 className="text-xs font-semibold text-amber-400 mb-2 flex items-center gap-1.5">
              <MessageSquare className="w-3 h-3" />
              Consumo estimado
            </h3>
            <div className="text-[11px] text-muted-foreground space-y-1.5">
              <div className="flex justify-between">
                <span>Generar proyecto</span>
                <span className="text-amber-300">~3,000 tokens</span>
              </div>
              <div className="flex justify-between">
                <span>Modificar proyecto</span>
                <span className="text-amber-300">~6,000 tokens</span>
              </div>
              <p className="text-[10px] text-muted-foreground/50 mt-2 leading-relaxed">
                Modificar cuesta mas porque la IA recibe el proyecto completo + tu instruccion. Generar solo recibe tu descripcion.
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIModifyPage;
